/**
 * diagnosticService.ts — API client for diagnostic procedure tracking.
 *
 * Calls the /api/diagnostic-procedures and /api/diagnostics/* routes.
 * Uses a dedicated axios instance (not the /admin-scoped api client)
 * because these routes live under /api/, not /api/admin/.
 */

import axios from 'axios';
import { getTokens, saveTokens, clearTokens } from './authApi';
import { logger } from '../utils/logger';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Procedure {
  id: number;
  slug: string;
  name: string;
  category: 'engine_mech' | 'electrical' | 'fuel' | 'inspection';
  inputs_schema: any;
  spec_kind: string | null;
  threshold_logic: any;
  display_config: any;
  parent_procedure_id: number | null;
}

export interface SpecUsed {
  min: number;
  max?: number;
  variance_limit?: number;
  // 'hub_free' is the live-Hub-agent source (vs 'hub_cache' for cached results) — the backend
  // distinguishes them but both render as CACHED on the badge.
  source: 'alldata' | 'hub_cache' | 'hub_free' | 'ai_estimate' | 'manual';
  retrieved_at: string;
  attribution_text: string;
  manual_override?: boolean;
}

export interface CellResult {
  cell: string;
  value: number;
  verdict: 'green' | 'yellow' | 'red';
  notes?: string;
}

export interface VehicleSnapshot {
  year: number;
  make: string;
  model: string;
  engine_size?: string;
  trim?: string;
  cylinder_count?: number;
  vin_last4?: string;
}

export interface Run {
  id: string;
  shop_id: number;
  work_order_id: number;
  work_order_line_id: number | null;
  procedure_id: number;
  tech_user_id: number;
  vehicle_snapshot: VehicleSnapshot;
  spec_used: SpecUsed | null;
  results: CellResult[];
  overall_verdict: 'green' | 'yellow' | 'red' | null;
  parent_run_id: string | null;
  photo_attachment_ids: number[];
  notes: string | null;
  status: 'draft' | 'completed' | 'void';
  created_at: string;
  completed_at: string | null;
}

// ─── Axios instance ───────────────────────────────────────────────────────────

// REACT_APP_API_URL is "<origin>/api"; the method paths below include the
// "/api/" prefix, so baseURL must be the ORIGIN — otherwise requests double up
// as "<origin>/api/api/..." → 404. Strip a trailing "/api" defensively.
const DIAG_API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:3001/api').replace(
  /\/api\/?$/,
  '',
);
const diagAxios = axios.create({
  baseURL: DIAG_API_ORIGIN,
  timeout: 15000,
});

diagAxios.interceptors.request.use(
  (config) => {
    const tokens = getTokens();
    if (tokens?.accessToken) {
      config.headers.Authorization = `Bearer ${tokens.accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

diagAxios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // 401 token-refresh handling — mirrors api.ts so long-running diagnostic
    // sessions (compression test + wet follow-up = 10-15 minutes) survive
    // access-token expiry without forcing a re-login.
    if (error.response?.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;
      try {
        const tokens = getTokens();
        if (tokens?.refreshToken) {
          const refreshResp = await axios.post(
            `${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/auth/refresh`,
            { refreshToken: tokens.refreshToken }
          );
          const { tokens: newTokens } = refreshResp.data;
          saveTokens(newTokens);
          originalRequest.headers.Authorization = `Bearer ${newTokens.accessToken}`;
          return diagAxios(originalRequest);
        }
      } catch (refreshError) {
        clearTokens();
        sessionStorage.setItem('session_expired', 'true');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    if (error.response) {
      logger.error('Diagnostic API Error:', {
        status: error.response.status,
        data: error.response.data,
        url: error.config?.url,
        method: error.config?.method,
      });
    } else if (error.request) {
      logger.error('Diagnostic API No Response:', {
        url: error.config?.url,
        method: error.config?.method,
      });
    } else {
      logger.error('Diagnostic API Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// ─── Service ──────────────────────────────────────────────────────────────────

export const diagnosticService = {
  /**
   * GET /api/diagnostic-procedures
   * List available diagnostic procedures, optionally filtered by vehicle.
   */
  listProcedures: (vehicle?: VehicleSnapshot) =>
    diagAxios.get<{ procedures: Procedure[] }>(
      '/api/diagnostic-procedures',
      vehicle ? { params: { vehicle: JSON.stringify(vehicle) } } : undefined
    ),

  /**
   * POST /api/work-orders/:woId/diagnostics
   * Create a new diagnostic run for a work order.
   */
  createRun: (
    woId: number,
    body: { procedureId: number; workOrderLineId?: number; parentRunId?: string }
  ) =>
    diagAxios.post<{ id: string; vehicleSnapshot: VehicleSnapshot }>(
      `/api/work-orders/${woId}/diagnostics`,
      body
    ),

  /**
   * GET /api/work-orders/:woId/diagnostics
   * List all diagnostic runs for a work order.
   */
  listForWorkOrder: (woId: number) =>
    diagAxios.get<{ runs: Run[] }>(`/api/work-orders/${woId}/diagnostics`),

  /**
   * GET /api/diagnostics/:id
   * Fetch a single diagnostic run by ID.
   */
  getRun: (id: string) =>
    diagAxios.get<Run>(`/api/diagnostics/${id}`),

  /**
   * PUT /api/diagnostics/:id
   * Save cell results (and optionally spec/notes) without completing the run.
   */
  updateResults: (
    id: string,
    body: { cells: CellResult[]; specUsed?: SpecUsed; notes?: string }
  ) =>
    diagAxios.put<{ ok: true }>(`/api/diagnostics/${id}`, body),

  /**
   * POST /api/diagnostics/:id/complete
   * Mark a run as completed and compute overall verdict.
   */
  complete: (id: string) =>
    diagAxios.post<Run>(`/api/diagnostics/${id}/complete`),

  /**
   * GET /api/diagnostics/:id/spec
   * Retrieve the spec used (or fetch it if not yet resolved).
   */
  getSpec: (id: string) =>
    diagAxios.get<{ spec: SpecUsed | null }>(`/api/diagnostics/${id}/spec`),

  /**
   * GET /api/diagnostics/:id/network-match
   * Find similar completed runs from the shop knowledge network.
   */
  networkMatch: (id: string) =>
    diagAxios.get<{ hits: any[] }>(`/api/diagnostics/${id}/network-match`),

  /**
   * POST /api/diagnostics/:id/photos
   * Attach an existing attachment record to a diagnostic run.
   */
  addPhoto: (id: string, attachmentId: number) =>
    diagAxios.post<{ ok: true }>(`/api/diagnostics/${id}/photos`, { attachmentId }),

  /**
   * Download the diagnostic run PDF as a Blob via the staff endpoint.
   * Use this when the user is signed in (staff context — DiagnosticDetail modal).
   *
   * TODO(Task 17): the CustomerPortalDiagnostic component CANNOT use this —
   * it has no staff JWT. Either (a) render the PDF in-browser via jsPDF
   * (already a dep), or (b) add a public `/api/public/work-orders/:token/
   * diagnostics/:id/pdf` endpoint in public-workorders.ts and a sibling
   * `portalDownloadPdf(token, runId)` method here.
   */
  downloadPdf: (id: string) =>
    diagAxios.get(`/api/diagnostics/${id}/pdf`, { responseType: 'blob' }),

  /**
   * Raw URL (no auth). Useful for debugging or copy-paste; NOT for direct
   * window.open() — that won't carry the Bearer token. Prefer downloadPdf().
   */
  pdfUrl: (id: string) => `/api/diagnostics/${id}/pdf`,
};
